import {
  ApolloCache,
  MutationFunctionOptions,
  useMutation as apolloUseMutation,
} from '@apollo/client'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { MutationPayload, handleMutation } from 'modules/core/lib/handleMutation'

type ApolloMutationParameters<
  TData,
  TVariables,
  TContext,
  TCache extends ApolloCache<unknown>
> = Parameters<typeof apolloUseMutation<TData, TVariables, TContext, TCache>>

export const useMutation = <
  TData,
  TVariables,
  TContext,
  TCache extends ApolloCache<unknown>,
  TOutput
>(
  document: ApolloMutationParameters<TData, TVariables, TContext, TCache>[0],
  options: ApolloMutationParameters<TData, TVariables, TContext, TCache>[1] & {
    extractorFn: (data: TData) => TOutput & MutationPayload
  }
) => {
  const { extractorFn, ...restOptions } = options
  const [performMutation, result] = apolloUseMutation(document, restOptions)

  /**
   * @param options.extractorFn - This function retrieves the mutation payload from
   *                              the GraphQL response data. This payload should
   *                              include the `errors` field.
   */
  const enhancedPerformMutation = useUnsafeCallback(
    (options: MutationFunctionOptions<TData, TVariables, TContext, TCache>) => {
      return handleMutation(performMutation(options), extractorFn)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [performMutation, extractorFn.toString(), options.toString()]
  )

  return [enhancedPerformMutation, result] as const
}
